import { useState, useCallback, useEffect, type FormEvent, type ChangeEvent } from 'react';
import { useNFTStore, useUIStore, useThemeStore } from '../stores';
import { CropModal } from '../components/crop';
import { ThemeToggle } from '../components/common/ThemeToggle';
import { FunLoadingText } from '../components/common/FunLoadingText';

export function CropperPage() {
  const [searchValue, setSearchValue] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  const { currentNFT, isLoading, error, fetchNFTByNum, setSearchQuery } = useNFTStore();
  const { openModal } = useUIStore();
  const { theme } = useThemeStore();

  // Apply theme on mount
  useEffect(() => {
    document.documentElement.classList.remove('light', 'dark');
    document.documentElement.classList.add(theme);
  }, [theme]);

  const handleSearchChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
    setSearchQuery(value);

    if (validationError && value.length > 0) {
      const num = parseInt(value, 10);
      if (!isNaN(num) && num > 0) {
        setValidationError(null);
      }
    }
  }, [setSearchQuery, validationError]);

  const incrementValue = () => {
    const current = parseInt(searchValue, 10) || 0;
    const newVal = (current + 1).toString();
    setSearchValue(newVal);
    setSearchQuery(newVal);
    setValidationError(null);
  };

  const decrementValue = () => {
    const current = parseInt(searchValue, 10) || 0;
    if (current > 1) {
      const newVal = (current - 1).toString();
      setSearchValue(newVal);
      setSearchQuery(newVal);
      setValidationError(null);
    }
  };

  const handleSubmit = useCallback(async (e: FormEvent) => {
    e.preventDefault();

    const num = parseInt(searchValue, 10);

    if (isNaN(num) || num <= 0) {
      setValidationError('Please enter a valid NFT number');
      return;
    }

    setValidationError(null);
    await fetchNFTByNum(num);
  }, [searchValue, fetchNFTByNum]);

  return (
    <div className="cropper-page min-h-screen bg-surface dark:bg-neutral-900 transition-colors duration-300">
      {/* Theme Toggle */}
      <div className="fixed top-4 right-4 z-50">
        <ThemeToggle />
      </div>

      {/* Main Content */}
      <div className="flex flex-col items-center px-4 py-8 max-w-md mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold mb-2">
            <span className="bg-gradient-to-r from-game-blue-dark to-game-blue bg-clip-text text-transparent">
              NUB PFP CROPPER
            </span>
          </h1>
          <p className="text-text-muted dark:text-neutral-400 text-sm sm:text-base">
            search for a nub and crop it for your profile picture
          </p>
        </div>

        {/* Search Form */}
        <form onSubmit={handleSubmit} className="w-full mb-6">
          <div className="flex gap-2 items-center">
            <div className="relative flex-1">
              <input
                type="number"
                value={searchValue}
                onChange={handleSearchChange}
                placeholder="enter nub #"
                disabled={isLoading}
                min="1"
                className="cropper-input w-full h-12 px-4 pr-10 rounded-lg text-lg"
              />
              {/* Stepper buttons */}
              <div className="absolute right-2 top-1/2 -translate-y-1/2 flex flex-col">
                <button
                  type="button"
                  onClick={incrementValue}
                  className="cropper-stepper-btn"
                  disabled={isLoading}
                >
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={decrementValue}
                  disabled={isLoading || parseInt(searchValue, 10) <= 1}
                  className="cropper-stepper-btn"
                >
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>
            </div>
            <button
              type="submit"
              disabled={isLoading || !searchValue.trim()}
              className="cropper-search-btn h-12 px-6 rounded-lg font-semibold"
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                'Search'
              )}
            </button>
          </div>
          {(validationError || error) && (
            <p className="text-red-400 text-sm mt-2 text-center">
              {validationError || error}
            </p>
          )}
        </form>

        {/* Loading State */}
        {isLoading && (
          <div className="text-center py-8">
            <div className="inline-flex items-center gap-3 text-text-muted dark:text-neutral-400">
              <div className="w-5 h-5 border-2 border-game-blue border-t-transparent rounded-full animate-spin" />
              <FunLoadingText />
            </div>
          </div>
        )}

        {/* NFT Display Area */}
        {currentNFT && !isLoading && (
          <div className="w-full animate-slide-up">
            {/* NFT Card */}
            <div className="relative flex justify-center mb-6">
              {/* NFT Image */}
              <div className="cropper-image-card p-2 rounded-xl">
                <div className="w-56 h-56 sm:w-64 sm:h-64 rounded-lg overflow-hidden">
                  <img
                    src={currentNFT.imageUrl}
                    alt={currentNFT.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>

            {/* Action Button */}
            <div className="flex justify-center">
              <button
                onClick={() => openModal('crop')}
                className="cropper-action-btn px-8 py-3 rounded-lg font-semibold"
              >
                Crop PFP
              </button>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!currentNFT && !isLoading && !error && (
          <div className="text-center py-12">
            <div className="w-24 h-24 mx-auto mb-4 rounded-xl bg-surface-secondary dark:bg-neutral-800 flex items-center justify-center">
              <svg className="w-10 h-10 text-text-muted dark:text-neutral-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <p className="text-text-muted dark:text-neutral-500 text-sm">
              enter a nub number to get started
            </p>
          </div>
        )}
      </div>

      {/* Crop Modal */}
      <CropModal mode="sendToChat" />
    </div>
  );
}

export default CropperPage;
